import React from 'react'

import apiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyContentSections,
  CompanyPageLayout,
} from 'components/NewCompanyPage'
import { UnmatchedDoorsModal } from 'components/NewCompanyPage/Modals/UnmatchedDoorsModal'

import { CompanyTypeSlug } from 'models/companies'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { ContactCompanyType } from 'models/contact_companies'
import { buildGooglePlaceSummaryContent } from './utils/googlePlacesHelpers'
import { buildLocationPlacesContent } from './utils/locationPlacesHelper'
import useDocumentTitle from 'components/useDocumentTitle'

export function NewConvenienceLocationPage({
  convenienceID,
  isLoading: isExternalLoading,
}: {
  convenienceID: number
  isLoading: boolean
}) {
  const api = apiService()
  const { data: doorProxyData, isLoading: isLoadingConvenienceDoor } =
    api.useGetConvenienceDoor(convenienceID)
  const { preferences } = usePreferences()
  const isLoading = isExternalLoading || isLoadingConvenienceDoor

  const chainProxyId = doorProxyData?.chain_proxy.id
  const { data: companyData } = api.useGetCompany(chainProxyId ?? 0)

  // Set document title
  useDocumentTitle(
    `${doorProxyData?.cstore?.name || 'Not Found'} (Convenience Store Door) - First Bite`
  )

  // Modal state management
  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  if (isLoading || !companyData) {
    return <LoadingScreen />
  }

  const summaryTabContent = buildGooglePlaceSummaryContent({
    placeData: doorProxyData?.cstore,
    onViewDoorsClick: () => setShowDoorsModal(true),
    hideDoorsMetric: true,
  })

  const locationDetailsTabContent = buildLocationPlacesContent({
    addressInfo: {
      city: doorProxyData?.cstore?.city,
      state: doorProxyData?.cstore?.state,
      street: doorProxyData?.cstore?.address,
      zipcode: doorProxyData?.cstore?.zipcode,
    },
    phone: doorProxyData?.cstore?.phone,
    googlePlaceUrl: doorProxyData?.cstore?.place_url,
  })

  return (
    <>
      <CompanyPageLayout
        chainProxyId={companyData?.id as number}
        breadcrumbRoot={companyData?.sub_category?.name || 'Convenience Stores'}
        breadcrumbs={[
          {
            title: 'Store',
            breadcrumb: companyData?.name || '',
            url: `/company/${preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated]}${chainProxyId}`,
          },
          {
            title: 'Door',
            breadcrumb: doorProxyData?.cstore?.name || '',
            url: undefined,
          },
        ]}
        sidebar={{
          header: {
            name: doorProxyData?.cstore?.name || '',
            domain: companyData?.domain || '',
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: locationDetailsTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={companyData?.id as number}
          doorProxyId={doorProxyData?.id}
          companyName={doorProxyData?.cstore?.name || ''}
          websiteAddress={companyData?.domain || ''}
          companyTypeSlug={companyData?.category as CompanyTypeSlug}
          tableTypeSlug="convenience-doors"
          isUserCreated={false}
          isDoorPage={true}
        />
      </CompanyPageLayout>

      {/* Unmatched Doors Modal */}
      {companyData && (
        <UnmatchedDoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          forChainProxyId={companyData?.id as number}
          chainData={{
            id: companyData?.id as number,
            name: companyData?.name || '',
            domain: companyData?.domain || '',
          }}
          subCategory={companyData?.sub_category}
          companyName={companyData?.name || ''}
        />
      )}
    </>
  )
}
